// Utility to categorize tags and apply universal categorization
import { useTagTaxonomyStore, type TagCategory } from "./tag-taxonomy-store"

export interface CategorizedTags {
  artists: string[]
  characters: string[]
  copyright: string[]
  series: string[]
  general: string[]
  meta: string[]
  va: string[]
  soundDesigners: string[]
}

/**
 * Categorize tags using the universal tag taxonomy system
 * Falls back to heuristic-based categorization for uncategorized tags
 * @param tags - Array of tag strings
 * @param booruCategories - Optional map of tag names to their booru-provided categories (from scraping)
 */
export function categorizeTags(
  tags: string[], 
  booruCategories?: Record<string, 'artist' | 'character' | 'copyright' | 'general' | 'meta'>
): CategorizedTags {
  const { getCategorization } = useTagTaxonomyStore.getState()

  const result: CategorizedTags = {
    artists: [],
    characters: [],
    copyright: [],
    series: [],
    general: [],
    meta: [],
    va: [],
    soundDesigners: [],
  }

  for (const tag of tags) {
    // Priority 1: Use booru-provided category (most accurate)
    if (booruCategories && booruCategories[tag]) {
      const booruCategory = booruCategories[tag]
      switch (booruCategory) {
        case 'artist':
          result.artists.push(tag)
          break
        case 'character':
          result.characters.push(tag)
          break
        case 'copyright':
          result.copyright.push(tag)
          break
        case 'meta':
          result.meta.push(tag)
          break
        case 'general':
        default:
          result.general.push(tag)
          break
      }
      continue
    }

    // Priority 2: Check Rule34 tag database (in-memory)
    if (typeof window !== 'undefined' && (window as any).__rule34TagDatabase) {
      const rule34Tags = (window as any).__rule34TagDatabase
      const rule34Category = rule34Tags[tag]
      
      if (rule34Category) {
        switch (rule34Category) {
          case 'artist':
            result.artists.push(tag)
            break
          case 'character':
            result.characters.push(tag)
            break
          case 'copyright':
            result.copyright.push(tag)
            break
          case 'meta':
            result.meta.push(tag)
            break
          case 'general':
          default:
            result.general.push(tag)
            break
        }
        continue
      }
    }

    // Priority 3: Check universal categorization (user-defined)
    const categorization = getCategorization(tag)

    if (categorization) {
      // Use the categorized tag
      switch (categorization.category) {
        case "artist":
          result.artists.push(tag)
          break
        case "character":
          result.characters.push(tag)
          break
        case "copyright":
          result.copyright.push(tag)
          break
        case "series":
          result.series.push(tag)
          break
        case "va":
          result.va.push(tag)
          break
        case "sound_designer":
          result.soundDesigners.push(tag)
          break
        case "meta":
          result.meta.push(tag)
          break
        default:
          result.general.push(tag)
      }
    } else {
      // Priority 4: Fallback to heuristic-based categorization
      const category = inferTagCategory(tag)
      switch (category) {
        case "artist":
          result.artists.push(tag)
          break
        case "character":
          result.characters.push(tag)
          break
        case "copyright":
        case "series":
          result.copyright.push(tag)
          break
        case "meta":
          result.meta.push(tag)
          break
        default:
          result.general.push(tag)
      }
    }
  }

  return result
}

/**
 * Infer tag category from tag string using heuristics
 * This is used as a fallback when no universal categorization exists
 */
function inferTagCategory(tag: string): TagCategory {
  const lowerTag = tag.toLowerCase()

  // Artist patterns
  if (
    lowerTag.includes("artist:") ||
    lowerTag.endsWith("_(artist)") ||
    lowerTag.includes("drawn_by_") ||
    lowerTag.includes("by_")
  ) {
    return "artist"
  }

  // Character patterns
  if (lowerTag.includes("character:") || lowerTag.includes("_(character)") || lowerTag.endsWith("_(cosplay)")) {
    return "character"
  }

  // Copyright/Series patterns
  if (
    lowerTag.includes("copyright:") ||
    lowerTag.includes("_(series)") ||
    lowerTag.includes("_(franchise)") ||
    lowerTag.includes("_(game)") ||
    lowerTag.includes("_(anime)")
  ) {
    return "series"
  }

  // Meta patterns
  if (
    lowerTag.startsWith("meta:") ||
    lowerTag.includes("rating:") ||
    lowerTag.includes("score:") ||
    lowerTag === "animated" ||
    lowerTag === "video" ||
    lowerTag === "gif"
  ) {
    return "meta"
  }

  // Default to general
  return "general"
}

/**
 * Auto-categorize tags based on booru site conventions
 * Some booru sites provide category information in their API
 */
export function autoCategorizeTags(tags: string[] | Record<string, number>): CategorizedTags {
  // Handle both array and object formats
  const tagArray = Array.isArray(tags) ? tags : Object.keys(tags)

  return categorizeTags(tagArray)
}

/**
 * Get category for a specific tag (uses universal taxonomy)
 */
export function getTagCategory(tag: string): TagCategory {
  const { getCategory } = useTagTaxonomyStore.getState()
  const category = getCategory(tag)
  
  // If not in taxonomy, use heuristic inference
  if (!category) {
    return inferTagCategory(tag)
  }
  
  return category
}

/**
 * Get color for tag category (for UI display)
 */
export function getTagCategoryColor(category: TagCategory): string {
  switch (category) {
    case "artist":
      return "text-red-500"
    case "character":
      return "text-green-500"
    case "copyright":
    case "series":
      return "text-purple-500"
    case "va":
      return "text-pink-500"
    case "sound_designer":
      return "text-orange-500"
    case "meta":
      return "text-blue-500"
    default:
      return "text-gray-800 dark:text-gray-100"
  }
}

/**
 * Get background color for tag category badges
 */
export function getTagCategoryBgColor(category: TagCategory): string {
  switch (category) {
    case "artist":
      return "bg-red-500/20"
    case "character":
      return "bg-green-500/20"
    case "copyright":
    case "series":
      return "bg-purple-500/20"
    case "va":
      return "bg-pink-500/20"
    case "sound_designer":
      return "bg-orange-500/20"
    case "meta":
      return "bg-blue-500/20"
    default:
      return "bg-gray-500/20"
  }
}

/**
 * Get color class for tag (wrapper for getTagCategoryColor)
 * Used by UI components for tag styling
 */
export function getTagColor(category: TagCategory): string {
  return getTagCategoryBgColor(category)
}

/**
 * Elevated variant, for side panels / info drawers. A light wash (the plain badge colour is the same hue at a higher
 * opacity, which made the coloured text hard to read), paired with getTagTextColorElevated below.
 */
export function getTagColorElevated(category: TagCategory): string {
  switch (category) {
    case "artist":       return "bg-red-500/15"
    case "character":    return "bg-green-500/15"
    case "copyright":
    case "series":       return "bg-purple-500/15"
    case "va":           return "bg-pink-500/15"
    case "sound_designer": return "bg-orange-500/15"
    case "meta":         return "bg-blue-500/15"
    default:             return "bg-gray-500/35"
  }
}

/**
 * Get text color class for tag
 * Used by UI components for tag text styling
 */
export function getTagTextColor(category: TagCategory): string {
  return getTagCategoryColor(category)
}

/**
 * Text colour for the elevated tag badges: a darker shade on light backgrounds and a lighter one on dark, so the label stays
 * readable on top of its own tinted background.
 */
export function getTagTextColorElevated(category: TagCategory): string {
  switch (category) {
    case "artist":       return "text-red-700 dark:text-red-300"
    case "character":    return "text-green-700 dark:text-green-300"
    case "copyright":
    case "series":       return "text-purple-700 dark:text-purple-300"
    case "va":           return "text-pink-700 dark:text-pink-300"
    case "sound_designer": return "text-orange-700 dark:text-orange-300"
    case "meta":         return "text-blue-700 dark:text-blue-300"
    default:             return "text-gray-800 dark:text-gray-100"
  }
}
